import config from 'config';

// reCAPTCHA Configuration Constants
export const RECAPTCHA_CONFIG = {
  DEFAULT_TIMEOUT_MS: 20000, // 20 seconds
  ERROR_MESSAGES: {
    NOT_READY: 'reCAPTCHA is not ready. Please reload the page.',
    TIMEOUT: 'reCAPTCHA timed out. Please try again.',
    NO_TOKEN: 'reCAPTCHA verification failed. Please try again.',
    EXPIRED: 'reCAPTCHA expired. Please try again.',
    EXPIRED_RESET_FAILED: 'reCAPTCHA expired and reset failed. Please reload the page and try again.',
    CONNECTION_ERROR: 'Could not connect to the reCAPTCHA service. Please check your internet connection and reload to get a reCAPTCHA challenge.',
    CONNECTION_RESET_FAILED: 'reCAPTCHA connection failed and reset failed. Please reload the page and try again.',
    RESET_FAILED: 'reCAPTCHA reset failed. Please reload the page and try again.',
  }
} as const;

// Type definitions
export interface RecaptchaRef {
  current: {
    executeAsync(): Promise<string>;
    reset(): void;
  } | null;
}

export interface MountedRef {
  current: boolean;
}

export interface ExecutingCaptchaRef {
  current: boolean;
}

export interface DispatchFunction {
  (action: any): void;
}

export interface ErrorActionFunction {
  (message: string): any;
}

export interface ExecuteRecaptchaParams {
  recaptchaRef: RecaptchaRef;
  mountedRef: MountedRef;
  executingCaptchaRef: ExecutingCaptchaRef;
  setIsExecutingCaptcha: (value: boolean) => void;
  dispatch: DispatchFunction;
  errorAction: ErrorActionFunction;
  timeoutMs?: number;
}

export interface CreateEventHandlerParams {
  recaptchaRef: RecaptchaRef;
  dispatch: DispatchFunction;
  errorAction: ErrorActionFunction;
}

/**
 * Safe wrapper to execute invisible reCAPTCHA with timeout and proper reset
 * @param params - Configuration parameters
 * @returns Promise<string|null> reCAPTCHA token or null
 */
export async function executeRecaptcha({
  recaptchaRef,
  mountedRef,
  executingCaptchaRef,
  setIsExecutingCaptcha,
  dispatch,
  errorAction,
  timeoutMs = RECAPTCHA_CONFIG.DEFAULT_TIMEOUT_MS
}: ExecuteRecaptchaParams): Promise<string | null> {
  if (!config.recaptcha.ENABLE_GOOGLE_RECAPTCHA) return null; // guard when disabled
  if (!recaptchaRef.current) {
    throw new Error('recaptcha_not_ready');
  }
  
  // Atomic check and set to prevent race conditions
  if (executingCaptchaRef.current) return null; // prevent concurrent executes
  executingCaptchaRef.current = true;
  if (mountedRef.current) setIsExecutingCaptcha(true);
  
  let timeoutId: ReturnType<typeof setTimeout>;
  try {
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error('recaptcha_timeout')), timeoutMs);
    });
    const token = await Promise.race([
      recaptchaRef.current.executeAsync(),
      timeoutPromise,
    ]);
    if (!token) throw new Error('recaptcha_no_token');
    return token;
  } finally {
    if (timeoutId!) clearTimeout(timeoutId);
    try {
      recaptchaRef.current && recaptchaRef.current.reset();
    } catch (err) {
      console.error('Error resetting reCAPTCHA:', err);
      // Inform user about reCAPTCHA reset failure
      if (mountedRef.current) {
        dispatch(errorAction(RECAPTCHA_CONFIG.ERROR_MESSAGES.RESET_FAILED));
      }
    }
    executingCaptchaRef.current = false;
    if (mountedRef.current) setIsExecutingCaptcha(false);
  }
}

/**
 * Handle reCAPTCHA expiration
 * @param params - Configuration parameters
 * @returns Function to handle reCAPTCHA expiration
 */
export function createOnRecaptchaExpired({ 
  recaptchaRef, 
  dispatch, 
  errorAction 
}: CreateEventHandlerParams): () => void {
  return () => {
    try {
      recaptchaRef.current && recaptchaRef.current.reset();
    } catch (err) {
      console.error('Error resetting reCAPTCHA on expired:', err);
      // Inform user about reCAPTCHA reset failure
      dispatch(errorAction(RECAPTCHA_CONFIG.ERROR_MESSAGES.EXPIRED_RESET_FAILED));
    }
  };
}

/**
 * Handle reCAPTCHA errors
 * @param params - Configuration parameters
 * @returns Function to handle reCAPTCHA errors
 */
export function createOnRecaptchaErrored({ 
  recaptchaRef, 
  dispatch, 
  errorAction 
}: CreateEventHandlerParams): () => void {
  return () => {
    try {
      recaptchaRef.current && recaptchaRef.current.reset();
    } catch (err) {
      console.error('Error resetting reCAPTCHA on error:', err);
      // Inform user about reCAPTCHA reset failure
      dispatch(errorAction(RECAPTCHA_CONFIG.ERROR_MESSAGES.CONNECTION_RESET_FAILED));
    }
  };
}

/**
 * Handle reCAPTCHA execution errors with specific error messages
 * @param error - The error that occurred
 * @param dispatch - Redux dispatch function
 * @param errorAction - Redux action to dispatch errors
 */
export function handleRecaptchaError(
  error: Error, 
  dispatch: DispatchFunction, 
  errorAction: ErrorActionFunction
): void {
  const code = error?.message || '';
  if (code === 'recaptcha_not_ready') {
    dispatch(errorAction(RECAPTCHA_CONFIG.ERROR_MESSAGES.NOT_READY));
  } else if (code === 'recaptcha_timeout') {
    dispatch(errorAction(RECAPTCHA_CONFIG.ERROR_MESSAGES.TIMEOUT));
  } else {
    dispatch(errorAction(RECAPTCHA_CONFIG.ERROR_MESSAGES.NO_TOKEN));
  }
}
